import * as turf from '@turf/turf';
import type { Feature } from 'geojson';
import { arcgisToGeoJSON } from '@terraformer/arcgis';
import * as projectOperator from '@arcgis/core/geometry/operators/projectOperator';
import { observability } from 'utils/observability';
import { SpatialReferenceWgs84 } from 'components/MapPage/CorporateViewTabs/LayersTab/utils/spatialReference';
import {
  buildGeoLocationFromGeoJson,
  type GeoLocation,
} from './buildGeoLocationFromGeoJson';

export const useLocationFromArcgisGeometry = (
  reverseGeocode: (
    longitude: number,
    latitude: number
  ) => Promise<{ address?: string; placeName?: string }>
) => {
  return async (
    geometry: __esri.Geometry,
    fallbackName?: string
  ): Promise<GeoLocation | undefined> => {
    await projectOperator.load();
    const projected = projectOperator.execute(
      geometry as __esri.GeometryUnion,
      SpatialReferenceWgs84
    );
    if (!projected) {
      observability.captureException(
        new Error('Projection failed in GeoLocationComponent')
      );
      return undefined;
    }

    const result = Array.isArray(projected) ? projected[0] : projected;
    const json = result?.toJSON?.() ?? null;
    const geoJson = json ? arcgisToGeoJSON(json) : null;

    if (
      !geoJson ||
      (geoJson.type !== 'Point' &&
        geoJson.type !== 'Polygon' &&
        geoJson.type !== 'MultiPolygon')
    ) {
      return undefined;
    }

    let centroidFeature: Feature;
    let centerLongLat: [number, number];

    if (geoJson.type === 'Point') {
      const [longitude, latitude] = geoJson.coordinates as [number, number];
      centerLongLat = [longitude, latitude];
      centroidFeature = {
        type: 'Feature',
        geometry: { type: 'Point', coordinates: centerLongLat },
        properties: {},
      };
    } else {
      const centroid = turf.centroid(geoJson);
      centerLongLat = centroid.geometry.coordinates as [number, number];
      centroidFeature = centroid;
    }

    const { address, placeName } = await reverseGeocode(
      centerLongLat[0],
      centerLongLat[1]
    );

    return buildGeoLocationFromGeoJson({
      geometry: geoJson,
      centroid: centroidFeature,
      address: address || fallbackName,
      placeName: placeName || fallbackName,
    });
  };
};
